import { Helmet } from "react-helmet-async";
import { Navbar } from "@/components/layout/Navbar";
import { AnchorIcon } from "@/components/icons/AnchorIcon";
import { Link } from "react-router-dom";
import { Separator } from "@/components/ui/separator";

const PrivacyPolicy = () => {
  const lastUpdated = "April 3, 2026";

  return (
    <div className="min-h-screen bg-background">
      <Helmet>
        <title>Privacy Policy | HostCaptain</title>
        <meta name="description" content="HostCaptain's privacy policy. Learn how we collect, use, and protect your personal information." />
        <link rel="canonical" href="https://hostcaptain.co/privacy" />
      </Helmet>

      <Navbar />

      <main className="container mx-auto px-6 py-16 max-w-3xl">
        <h1 className="text-3xl font-bold mb-2">Privacy Policy</h1>
        <p className="text-muted-foreground mb-8">Last updated: {lastUpdated}</p>

        <div className="prose prose-slate dark:prose-invert max-w-none space-y-8">

          {/* Introduction */}
          <section>
            <h2 className="text-xl font-semibold">Introduction</h2>
            <p>
              HostCaptain is a product of <strong>MC Capital Solutions LLC</strong>, a company registered in the State of California, United States. This Privacy Policy explains how we collect, use, store, and protect your personal information when you use the HostCaptain platform ("Service").
            </p>
            <p>
              By using HostCaptain, you agree to the collection and use of information in accordance with this policy. If you do not agree with this policy, please do not use the Service.
            </p>
          </section>

          <Separator />

          {/* Information We Collect */}
          <section>
            <h2 className="text-xl font-semibold">Information We Collect</h2>
            <p>We collect the following types of information to provide and improve the Service:</p>

            <h3 className="text-lg font-medium mt-4">Account Information</h3>
            <ul className="list-disc pl-6 space-y-1">
              <li>Name and email address (provided during registration)</li>
              <li>Password (stored securely using industry-standard hashing)</li>
            </ul>

            <h3 className="text-lg font-medium mt-4">Business Data You Provide</h3>
            <ul className="list-disc pl-6 space-y-1">
              <li>Vehicle information (make, model, year, VIN, mileage, purchase price, etc.)</li>
              <li>Trip and earnings data (imported from Turo CSV exports)</li>
              <li>Expense records, receipts, and maintenance logs</li>
              <li>Loan and recurring payment details</li>
              <li>Team member information (name and email for invitations)</li>
            </ul>

            <h3 className="text-lg font-medium mt-4">Usage Data</h3>
            <ul className="list-disc pl-6 space-y-1">
              <li>Pages visited, features used, and interactions within the app</li>
              <li>Browser type, device type, and operating system</li>
              <li>IP address and approximate geographic location</li>
            </ul>

            <h3 className="text-lg font-medium mt-4">Cookies and Analytics</h3>
            <p>
              We use cookies and similar tracking technologies to analyze usage patterns and improve the Service. We use third-party analytics services including Segment and PostHog for this purpose. You can control cookie preferences through your browser settings.
            </p>
          </section>

          <Separator />

          {/* How We Use Your Information */}
          <section>
            <h2 className="text-xl font-semibold">How We Use Your Information</h2>
            <p>
              All information we collect is used <strong>strictly to provide, maintain, and improve the Service</strong>. Specifically, we use your information to:
            </p>
            <ul className="list-disc pl-6 space-y-1">
              <li>Provide and operate the HostCaptain platform</li>
              <li>Generate financial reports, dashboards, and analytics for your business</li>
              <li>Power AI-assisted features such as Captain AI recommendations</li>
              <li>Process payments and manage your subscription</li>
              <li>Send transactional emails (account verification, password resets, team invitations)</li>
              <li>Respond to support inquiries</li>
              <li>Improve the Service based on usage patterns and feedback</li>
            </ul>
          </section>

          <Separator />

          {/* What We Do NOT Do */}
          <section>
            <h2 className="text-xl font-semibold">What We Do NOT Do</h2>
            <div className="bg-primary/5 border border-primary/20 rounded-lg p-4">
              <ul className="list-disc pl-6 space-y-1 mb-0">
                <li><strong>We do not sell your personal information</strong> to third parties — ever.</li>
                <li>We do not share your data with advertisers or data brokers.</li>
                <li>We do not use your business data to compete with you or share it with other hosts.</li>
                <li>We do not rent or trade your information for marketing purposes.</li>
              </ul>
            </div>
          </section>

          <Separator />

          {/* Payment Processing & Stripe */}
          <section>
            <h2 className="text-xl font-semibold">Payment Processing</h2>
            <p>
              Payments for HostCaptain subscriptions are processed by <strong>Stripe, Inc.</strong> ("Stripe"). When you subscribe to a paid plan:
            </p>
            <ul className="list-disc pl-6 space-y-1">
              <li>Your credit card details are entered directly into Stripe's secure payment form.</li>
              <li><strong>HostCaptain does not store, process, or have access to your full credit card number.</strong></li>
              <li>Stripe is PCI-DSS Level 1 certified, the highest level of payment security certification.</li>
              <li>We receive only a limited record from Stripe (e.g., last four digits of your card, billing email) to display subscription status.</li>
            </ul>
            <p>
              For more information, see{" "}
              <a href="https://stripe.com/privacy" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">
                Stripe's Privacy Policy
              </a>.
            </p>
          </section>

          <Separator />

          {/* Stripe Financial Connections */}
          <section>
            <h2 className="text-xl font-semibold">Bank Account Linking (Stripe Financial Connections)</h2>
            <p>
              HostCaptain may offer the ability to link your bank or credit card accounts to automatically import transactions. This feature is powered by{" "}
              <strong>Stripe Financial Connections</strong>, a service provided by Stripe, Inc.
            </p>

            <h3 className="text-lg font-medium mt-4">How It Works</h3>
            <ul className="list-disc pl-6 space-y-1">
              <li>When you choose to link a financial account, you are redirected to Stripe's secure interface to authorize the connection.</li>
              <li>HostCaptain <strong>never receives your bank login credentials</strong>. Authentication is handled entirely by Stripe and your financial institution.</li>
              <li>Once authorized, Stripe provides HostCaptain with read-only access to your transaction history (up to 180 days) for the connected account(s).</li>
            </ul>

            <h3 className="text-lg font-medium mt-4">What Data We Receive</h3>
            <ul className="list-disc pl-6 space-y-1">
              <li>Transaction details: date, amount, merchant/description, and category</li>
              <li>Account metadata: account name, type (checking, credit card), and last four digits</li>
              <li>We do <strong>not</strong> receive your account balance, account number, or routing number</li>
            </ul>

            <h3 className="text-lg font-medium mt-4">How We Use This Data</h3>
            <ul className="list-disc pl-6 space-y-1">
              <li>To display your transactions within HostCaptain for review</li>
              <li>To analyze transactions and suggest or auto-create expense records for your business</li>
              <li>To help you categorize spending and assign expenses to specific vehicles</li>
            </ul>

            <h3 className="text-lg font-medium mt-4">Your Control</h3>
            <ul className="list-disc pl-6 space-y-1">
              <li>Linking a bank account is entirely optional and always requires your explicit consent.</li>
              <li>You can disconnect a linked account at any time from your HostCaptain settings.</li>
              <li>Upon disconnection, we stop receiving new transaction data. You may also request deletion of previously imported transaction data.</li>
            </ul>

            <p>
              For more information, see{" "}
              <a href="https://stripe.com/legal/financialconnections" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">
                Stripe Financial Connections Terms
              </a>{" "}
              and the{" "}
              <a href="https://stripe.com/legal/financial-connections-security-addendum" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">
                Financial Connections Security Addendum
              </a>.
            </p>
          </section>

          <Separator />

          {/* Third-Party Services */}
          <section>
            <h2 className="text-xl font-semibold">Third-Party Services</h2>
            <p>We use the following third-party services to operate and improve HostCaptain:</p>
            <ul className="list-disc pl-6 space-y-1">
              <li><strong>Supabase</strong> — Database hosting, authentication, and file storage</li>
              <li><strong>Stripe</strong> — Payment processing and financial account connections</li>
              <li><strong>Segment</strong> — Analytics event routing</li>
              <li><strong>PostHog</strong> — Product analytics and usage insights</li>
              <li><strong>Cloudflare</strong> — Content delivery, DDoS protection, and Turnstile CAPTCHA</li>
              <li><strong>ActiveCampaign</strong> — Transactional and marketing email delivery</li>
            </ul>
            <p>
              Each of these services has their own privacy policy governing how they handle data. We only share the minimum data necessary for each service to function.
            </p>
          </section>

          <Separator />

          {/* Data Storage and Security */}
          <section>
            <h2 className="text-xl font-semibold">Data Storage and Security</h2>
            <ul className="list-disc pl-6 space-y-1">
              <li>All user data is stored on servers located in the <strong>United States</strong>.</li>
              <li>Data is encrypted in transit using TLS/SSL and at rest using AES-256 encryption.</li>
              <li>Access to production systems is restricted to authorized personnel only.</li>
              <li>We use Row-Level Security (RLS) policies to ensure users can only access their own data and data belonging to their company.</li>
              <li>We conduct regular security reviews of our codebase and infrastructure.</li>
            </ul>
          </section>

          <Separator />

          {/* Data Retention */}
          <section>
            <h2 className="text-xl font-semibold">Data Retention</h2>
            <p>
              We retain your data for as long as your account is active or as needed to provide the Service. If you delete your account, we will delete your personal data within 30 days, except where we are required to retain it by law (e.g., financial records for tax purposes).
            </p>
            <p>
              Anonymized and aggregated data that cannot be used to identify you may be retained indefinitely for analytics and product improvement.
            </p>
          </section>

          <Separator />

          {/* Your Rights */}
          <section>
            <h2 className="text-xl font-semibold">Your Rights</h2>
            <p>Depending on your location, you may have the following rights regarding your personal data:</p>
            <ul className="list-disc pl-6 space-y-1">
              <li><strong>Access:</strong> Request a copy of the personal data we hold about you.</li>
              <li><strong>Correction:</strong> Request correction of inaccurate or incomplete data.</li>
              <li><strong>Deletion:</strong> Request deletion of your personal data (subject to legal retention requirements).</li>
              <li><strong>Portability:</strong> Request an export of your data in a machine-readable format.</li>
              <li><strong>Objection:</strong> Object to certain types of data processing.</li>
              <li><strong>Withdrawal of Consent:</strong> Withdraw consent for optional data processing at any time.</li>
            </ul>

            <h3 className="text-lg font-medium mt-4">California Residents (CCPA/CPRA)</h3>
            <p>
              If you are a California resident, you have additional rights under the California Consumer Privacy Act (CCPA) and the California Privacy Rights Act (CPRA), including the right to know what personal information we collect, the right to delete it, and the right to opt out of the sale of personal information. <strong>We do not sell personal information.</strong>
            </p>

            <h3 className="text-lg font-medium mt-4">European Economic Area Residents (GDPR)</h3>
            <p>
              If you are located in the EEA, we process your data based on legitimate interests (providing the Service), contractual necessity, and your consent where applicable. You have the right to lodge a complaint with your local data protection authority.
            </p>
          </section>

          <Separator />

          {/* Children's Privacy */}
          <section>
            <h2 className="text-xl font-semibold">Children's Privacy</h2>
            <p>
              HostCaptain is not intended for use by anyone under the age of 18. We do not knowingly collect personal information from children. If we become aware that a child under 18 has provided us with personal data, we will take steps to delete such information promptly.
            </p>
          </section>

          <Separator />

          {/* Changes to This Policy */}
          <section>
            <h2 className="text-xl font-semibold">Changes to This Policy</h2>
            <p>
              We may update this Privacy Policy from time to time to reflect changes in our practices, technology, or legal requirements. When we make material changes, we will notify you by email or by posting a prominent notice on the Service. Your continued use of HostCaptain after any changes constitutes acceptance of the updated policy.
            </p>
          </section>

          <Separator />

          {/* Contact Us */}
          <section>
            <h2 className="text-xl font-semibold">Contact Us</h2>
            <p>If you have any questions about this Privacy Policy or your personal data, please contact us at:</p>
            <div className="bg-card border rounded-lg p-4 mt-2">
              <p className="font-semibold mb-1">MC Capital Solutions LLC</p>
              <p className="text-muted-foreground mb-1">DBA HostCaptain</p>
              <p className="mb-0">
                Email:{" "}
                <a href="mailto:support@hostcaptain.co" className="text-primary hover:underline">
                  support@hostcaptain.co
                </a>
              </p>
            </div>
          </section>

        </div>
      </main>

      {/* Footer */}
      <footer className="bg-navy text-white py-12 mt-16">
        <div className="container mx-auto px-6">
          <div className="flex flex-col md:flex-row justify-between items-center">
            <div className="flex items-center gap-2 mb-4 md:mb-0">
              <AnchorIcon className="h-6 w-6" />
              <span className="font-bold">HostCaptain</span>
            </div>
            <div className="flex gap-8 text-sm text-white/60">
              <Link to="/pricing" className="hover:text-white transition-colors">Pricing</Link>
              <Link to="/privacy" className="hover:text-white transition-colors">Privacy Policy</Link>
              <Link to="/terms" className="hover:text-white transition-colors">Terms of Service</Link>
              <Link to="/auth" className="hover:text-white transition-colors">Login</Link>
            </div>
          </div>
          <div className="mt-6 text-center text-xs text-white/40">
            By using HostCaptain, you agree to our{" "}
            <Link to="/privacy" className="hover:text-white/60 underline">Privacy Policy</Link> and{" "}
            <Link to="/terms" className="hover:text-white/60 underline">Terms of Service</Link>.
          </div>
          <div className="mt-4 pt-4 border-t border-white/10 text-center text-sm text-white/40">
            © {new Date().getFullYear()} HostCaptain. All rights reserved.
          </div>
        </div>
      </footer>
    </div>
  );
};

export default PrivacyPolicy;
